import React from "react";
import { Helmet } from "react-helmet-async";
import { useTranslation } from "react-i18next";

/**
 * SeoHead
 *
 * Emits title, description, canonical, hreflang alternates, Open Graph,
 * Twitter cards and optional JSON-LD structured data.  All URLs are
 * canonicalised on `https://mytharion.app` (production origin) regardless
 * of the preview host, so Google always indexes the right host.
 *
 * Language handling:
 *   - `path` is the language-agnostic path (e.g. `/store`, `/cartomancer`).
 *   - The active language comes from i18next.
 *   - We emit `<link rel="alternate" hreflang="…">` for both EN and IT
 *     pointing at `/en/<path>` and `/it/<path>` respectively, plus
 *     `x-default` → `/en/<path>` (English is the primary SEO target).
 *   - `canonical` points to the current language variant.
 */
const CANONICAL_HOST = "https://mytharion.app";

const stripLangPrefix = (p = "/") => {
  if (!p.startsWith("/")) p = `/${p}`;
  const m = p.match(/^\/(en|it)(\/.*)?$/);
  if (!m) return p;
  return m[2] || "/";
};

export default function SeoHead({
  title,
  description,
  path = "/",
  image = `${CANONICAL_HOST}/og-default.jpg`,
  type = "website",
  noindex = false,
  jsonLd = null,
}) {
  const { i18n } = useTranslation();
  const lang = (i18n.language || "en").split("-")[0];
  const cleanPath = stripLangPrefix(path);

  const localised = (l) =>
    cleanPath === "/" ? `${CANONICAL_HOST}/${l}/` : `${CANONICAL_HOST}/${l}${cleanPath}`;

  const canonicalUrl = localised(lang === "it" ? "it" : "en");
  const enUrl = localised("en");
  const itUrl = localised("it");

  const fullTitle = title
    ? `${title} — Mytharion`
    : lang === "it"
    ? "Mytharion — Virtual Tabletop e strumenti per Game Master GDR"
    : "Mytharion — Virtual Tabletop and Game Master tools for TTRPGs";

  const ogLocale = lang === "it" ? "it_IT" : "en_US";

  return (
    <Helmet htmlAttributes={{ lang }}>
      <title>{fullTitle}</title>
      {description && <meta name="description" content={description} />}
      <link rel="canonical" href={canonicalUrl} />
      {noindex && <meta name="robots" content="noindex, nofollow" />}

      {/* hreflang alternates — English is the SEO primary. */}
      <link rel="alternate" hrefLang="en" href={enUrl} />
      <link rel="alternate" hrefLang="it" href={itUrl} />
      <link rel="alternate" hrefLang="x-default" href={enUrl} />

      {/* Open Graph */}
      <meta property="og:type" content={type} />
      <meta property="og:site_name" content="Mytharion" />
      <meta property="og:title" content={fullTitle} />
      {description && <meta property="og:description" content={description} />}
      <meta property="og:url" content={canonicalUrl} />
      <meta property="og:locale" content={ogLocale} />
      <meta
        property="og:locale:alternate"
        content={ogLocale === "en_US" ? "it_IT" : "en_US"}
      />
      <meta property="og:image" content={image} />

      {/* Twitter */}
      <meta name="twitter:card" content="summary_large_image" />
      <meta name="twitter:title" content={fullTitle} />
      {description && <meta name="twitter:description" content={description} />}
      <meta name="twitter:image" content={image} />

      {jsonLd && (
        <script type="application/ld+json">{JSON.stringify(jsonLd)}</script>
      )}
    </Helmet>
  );
}
